import { v4 } from 'uuid';
import axios from 'axios';

import { Analytics } from '../../../ModularReduxAnalytics';
import { AsyncLogicParams, REGISTRATION_URL, HOST_URL } from './index';
import AccountManagementService from '../../../services/accountManagement';

let cancelToken;

const registrationLogic: Array<AsyncLogicParams> = [
  {
    name: 'checkValidEmail',
    axiosConfig: (email: string, publicRuntimeConfig, getState) => ({
      method: 'get',
      url: `${HOST_URL(publicRuntimeConfig)}/valid-email?email=${email}`,
      headers: {
        'client-ip-address': getState().user.ipAddress
      }
    }),
    shouldSetGlobalAsyncProperties: false
  },
  {
    name: 'complete',
    axiosConfig: (payload, publicRuntimeConfig, getState) => ({
      method: 'post',
      url: `${REGISTRATION_URL(publicRuntimeConfig)}/complete`,
      data: {},
      headers: {
        Authorization: `Bearer ${getState().app.registrationToken}`,
        'segment-anonymous-id':
          (window as any).analytics &&
          typeof (window as any).analytics.user === 'function'
            ? (window as any).analytics.user().anonymousId()
            : Analytics.getAnonymousId() || v4()
      }
    }),
    registrationStep: 'complete'
  },
  {
    name: 'emailVerification',
    axiosConfig: (payload, publicRuntimeConfig) => ({
      method: 'get',
      url: `${HOST_URL(publicRuntimeConfig)}/user/verify-email`,
      data: {
        params: {
          tokenized_link: payload.verificationToken,
          user_id: payload.userId
        }
      }
    })
  },
  {
    name: 'getAnswers',
    axiosConfig: (payload, publicRuntimeConfig, getState) => ({
      method: 'get',
      url: `${REGISTRATION_URL(publicRuntimeConfig)}/answers`,
      headers: {
        Authorization: `Bearer ${getState().app.registrationToken}`,
        Pragma: 'no-cache',
        'x-test-tracer': v4(),
        'client-ip-address': getState().user.ipAddress
      },
      registrationStep: 'widget_page'
    }),
    shouldSetGlobalAsyncProperties: false
  },
  {
    name: 'getPricingPlan',
    promise: AccountManagementService.getPricingPlan,
    registrationStep: 'set_plan',
    shouldSetGlobalAsyncProperties: false
  },
  {
    name: 'getUser',
    axiosConfig: (
      registrationToken: string | undefined,
      publicRuntimeConfig,
      getState
    ) => ({
      method: 'get',
      url: `${HOST_URL(publicRuntimeConfig)}/user`,
      headers: {
        Authorization: `Bearer ${
          registrationToken || getState().app.registrationToken
        }`,
        'client-ip-address': getState().user.ipAddress
      },
      registrationStep: 'webreg' // @todo: find out if this is right
    }),
    shouldSetGlobalAsyncProperties: false
  },
  {
    name: 'queryGetUser',
    axiosConfig: (payload, publicRuntimeConfig, getState) => ({
      method: 'get',
      url: `${REGISTRATION_URL(publicRuntimeConfig)}/query?actionType=get_user`,
      headers: {
        Authorization: `Bearer ${getState().app.registrationToken}`,
        'x-test-tracer': v4(),
        'client-ip-address': getState().user.ipAddress
      },
      registrationStep: 'webreg' // @todo: find out if this is right
    }),
    shouldSetGlobalAsyncProperties: false
  },
  {
    name: 'resolveError',
    axiosConfig: (payload, publicRuntimeConfig, getState) => ({
      method: 'put',
      url: `${REGISTRATION_URL(publicRuntimeConfig)}/resolve-error?error_id=${
        payload.errorId
      }`,
      data: {},
      headers: {
        Authorization: `Bearer ${getState().app.registrationToken}`,
        'segment-anonymous-id':
          (window as any).analytics &&
          typeof (window as any).analytics.user === 'function'
            ? (window as any).analytics.user().anonymousId()
            : Analytics.getAnonymousId() || v4(),
        'client-ip-address': getState().user.ipAddress
      }
    }),
    registrationStep: 'widget_page'
  },
  {
    name: 'verifyPromoCode',
    axiosConfig: (payload, publicRuntimeConfig, getState) => {
      if (typeof cancelToken !== typeof undefined) {
        cancelToken.cancel('Operation canceled due to new request.');
      }
      cancelToken = axios.CancelToken.source();
      return {
        method: 'get',
        cancelToken: cancelToken.token,
        url: `${REGISTRATION_URL(publicRuntimeConfig)}/verify-code/?code=${
          payload.code
        }`,
        headers: {
          'client-ip-address': getState().user.ipAddress
        }
      };
    },
    registrationStep: 'promo_code'
  },
  {
    name: 'validatePlaceholderToken',
    axiosConfig: (payload, publicRuntimeConfig, getState) => ({
      method: 'get',
      url: `${HOST_URL(publicRuntimeConfig)}/user/${
        payload.userId
      }/pre-registered`,
      params: {
        token: payload.placeholderToken
      },
      headers: {
        Authorization: `Bearer ${getState().app.registrationToken}`,
        'x-test-tracer': v4(),
        'client-ip-address': getState().user.ipAddress
      }
    })
  },
  {
    name: 'getFailureReason',
    axiosConfig: payload => ({
      method: 'get',
      url: '/api/kyc-failure-type',
      params: {
        identityId: payload.userId
      }
    }),
    shouldSetGlobalAsyncProperties: false,
    registrationStep: 'verify_identity'
  },
  {
    name: 'subscribeSMS',
    axiosConfig: (payload, _publicRuntimeConfig, getState) => ({
      method: 'post',
      url: '/api/subscribe-sms',
      data: {
        userId: `${_publicRuntimeConfig.analyticsPrefix}${payload.userId}`,
        subscriptionType: payload.subscriptionType
      },
      headers: {
        'x-test-tracer': v4(),
        'client-ip-address': getState().user.ipAddress
      }
    }),
    shouldSetGlobalAsyncProperties: false
  },
  {
    name: 'trackCustomEvent',
    axiosConfig: (payload, _publicRuntimeConfig, getState) => ({
      method: 'post',
      url: '/api/track-custom-event',
      data: {
        userId: `${_publicRuntimeConfig.analyticsPrefix}${
          payload.userId ?? ''
        }`,
        ...payload
      },
      headers: {
        'x-test-tracer': v4(),
        'client-ip-address': getState().user.ipAddress
      }
    }),
    shouldSetGlobalAsyncProperties: false
  },
  {
    name: 'identifyUser',
    axiosConfig: (payload, _publicRuntimeConfig, getState) => ({
      method: 'post',
      url: '/api/identify-users',
      data: payload,
      headers: {
        'x-test-tracer': v4(),
        'client-ip-address': getState().user.ipAddress
      }
    }),
    shouldSetGlobalAsyncProperties: false
  }
];

export default registrationLogic;
